import {
	ChangeDetectionStrategy,
	Component,
	inject,
	OnInit,
} from '@angular/core';
import { MissingFieldSection } from '../stewart/services/validate-stewart-data.service';
import { Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BusinessState } from '@state/business';
import { Store } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { MatterClients } from '@state/global/matter_clients';
import { PropertyType, CondominiumType } from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { MatDialog } from '@angular/material/dialog';
import { AddTitleInsuranceComponent } from '../components/add-title-insurance/add-title-insurance.component';
import { ValidateTitlePLUSService } from './services/validate-title-plus-data.service';
import { filter, take } from 'rxjs/operators';
import { TitlePlusService } from '@services/title-plus/title-plus.service';
import { TitlePlusModalComponent } from './components/title-plus-modal/title-plus-modal.component';
import {
	TitlePlusCancelModalComponent,
	TitlePlusCancelModalData,
} from './components/title-plus-cancel-modal/title-plus-cancel-modal.component';
import { BlankTitleInsurance } from '../chicago/chicago.component';
import { checkForDuplicatePriority } from '@core/util/title-insurance';
import { Step } from 'src/app/shared/atomic/molecules/step-progress-nav/step-progress-nav.component';
import { ActivatedRoute } from '@angular/router';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { isRegionOntario } from '@core/util/address.utils';
import { TestHooks } from './title-plus.hooks';
import { TitlePlusStatus } from '@models/title-insurance';
import { TitlePlusViewModel } from './title-plus.view-model';
import { MatterTitlePlusTitleInsurance } from '@models/title-insurance/title-plus';

const Steps: Step[] = [
	{ title: 'Preparing' },
	{ title: 'Submitted' },
	{ title: 'Processing' },
	{ title: 'Completed' },
];

@UntilDestroy()
@Component({
	selector: 'app-title-plus',
	templateUrl: './title-plus.component.html',
	styleUrls: ['./title-plus.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class TitlePlusComponent implements OnInit {
	testHooks = TestHooks;
	public store = inject(Store);
	public allowSwitchProviders = true;
	public loading = false;
	public status: TitlePlusStatus;
	public orderValid = false;
	public missing: MissingFieldSection[] = [];
	public TitlePlusStatus = TitlePlusStatus;
	public canSubmit = true;
	public blankTitleInsurance = BlankTitleInsurance;
	public outOfOntario = false;
	public isLeasehold = false;
	public showPriorityWarning = false;
	public isOther = false;
	public isMobileHome = false;
	public changePropertyButtonText: string;
	public steps = Steps;
	public currentStep = 0;

	nameFormattingSettings = this.store.selectSnapshot(
		BusinessState.getNameFormattingSettings,
	);
	matter$ = this.store.select(SelectedMatter.getSelected);
	clients$ = this.store.select(
		MatterClients.get_mortgagorsWithAssociationMapped,
	);
	matterId = this.store.selectSnapshot(SelectedMatter.id);
	assignedLawyer = this.store.selectSnapshot(SelectedMatter.assignedLawyer);

	thirdPartyId = this.store.selectSnapshot(
		SelectedMatter.titleInsuranceThirdPartyId,
	);
	state$ = this.store.select(SelectedMatter.getTitleInsuranceState);

	titleInsurance$ = this.store.select(
		SelectedMatter.getTitleInsurance,
	) as Observable<MatterTitlePlusTitleInsurance>;
	titlePlusPayloadProps$ = this.store.select(
		TitlePlusViewModel.titlePlusPayloadProps,
	);

	constructor(
		private dialog: MatDialog,
		private validateTitlePLUSService: ValidateTitlePLUSService,
		private titlePlusService: TitlePlusService,
		private matterDispatch: MatterDispatch,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
	) {}

	ngOnInit() {
		this.state$.pipe(untilDestroyed(this)).subscribe((_state) => {
			if (_state) {
				this.status = _state as TitlePlusStatus;
				this.setAllowSwitchProviders(this.status);
				this.setCurrentStep(this.status);
				if (
					[
						TitlePlusStatus.DECLINED,
						TitlePlusStatus.DELETED,
						TitlePlusStatus.CANCELLED,
					].includes(this.status)
				) {
					this.steps.pop();
					if (this.status === TitlePlusStatus.DECLINED) {
						this.steps.push({ title: 'Declined', failed: true });
					} else if (this.status === TitlePlusStatus.DELETED) {
						this.steps.push({ title: 'Deleted', failed: true });
					} else if (this.status === TitlePlusStatus.CANCELLED) {
						this.steps.push({ title: 'Cancelled', failed: true });
					}

					this.changeStepperColor('failed');
				} else if (
					this.status === TitlePlusStatus.USER_ACTION_REQUIRED ||
					(!this.orderValid && this.status === TitlePlusStatus.NOT_SUBMITTED)
				) {
					this.changeStepperColor('warning');
				} else {
					this.changeStepperColor();
				}
			}
		});
		this.titleInsurance$
			.pipe(untilDestroyed(this), take(1))
			.subscribe(async (_titleInsurance) => {
				if (_titleInsurance.thirdPartyId) {
					try {
						await this.titlePlusService.updateStatus();
					} catch (error) {
						LogRocket.log('Error getting Title plus status details', error);
					} finally {
						this.loading = false;
					}
				}
			});

		this.titlePlusPayloadProps$
			.pipe(untilDestroyed(this))

			.subscribe((titlePlusPayloadProps) => {
				const {
					matter,
					clients,
					mortgages,
					remainEncumbrances,
					matterTitles,
					phoneNumber,
					lawyerConnected,
				} = titlePlusPayloadProps;

				if (!matter) {
					return;
				}

				const propertyType = matter.propertyDetails?.propertyType?.type;
				const propertyUsage = matter.propertyDetails?.propertyType?.usage;

				this.outOfOntario = !isRegionOntario(
					matter?.propertyDetails?.address?.province,
				);

				this.isOther =
					propertyUsage === PropertyUsage.RESIDENTIAL &&
					propertyType === PropertyType.OTHER;
				this.isMobileHome =
					propertyUsage === PropertyUsage.RESIDENTIAL &&
					propertyType === PropertyType.MOBILE_HOME;
				this.isLeasehold =
					propertyUsage === PropertyUsage.COMMERCIAL &&
					propertyType === PropertyType.CONDO &&
					matter?.propertyDetails?.propertyType?.condoType ===
						CondominiumType.LEASEHOLD;

				this.showPriorityWarning = !!checkForDuplicatePriority(
					mortgages,
					remainEncumbrances,
				);

				const missingOrderFields =
					this.validateTitlePLUSService.getSubmitDataMissingSections(
						matter,
						clients,
						mortgages,
						remainEncumbrances,
						this.nameFormattingSettings,
						matterTitles,
						phoneNumber,
						lawyerConnected,
					);

				this.orderValid =
					missingOrderFields?.length === 0 &&
					!this.isOther &&
					!this.isMobileHome &&
					!this.outOfOntario &&
					!this.isLeasehold;

				if (!this.orderValid) {
					LogRocket.log('Missing fields', missingOrderFields);
				}

				this.missing = missingOrderFields;
				this.canSubmit = !(
					this.isOther ||
					this.isMobileHome ||
					this.outOfOntario ||
					this.isLeasehold
				);
				if (this.isOther || this.isMobileHome || this.isLeasehold) {
					this.changePropertyButtonText = 'Change property type';
				} else if (this.outOfOntario) {
					this.changePropertyButtonText = 'Change property address';
				}
			});

		this.route.queryParams
			.pipe(untilDestroyed(this))
			.subscribe((queryparams) => {
				const { modal } = queryparams;

				if (modal === 'submit') {
					this.submitFile();
				}
			});
	}

	submitFile() {
		if (
			this.status === TitlePlusStatus.DECLINED ||
			this.status === TitlePlusStatus.DELETED
		) {
			return;
		}

		if (!this.status) {
			//means new policy
			this.analytics.logEvent(AnalyticsEvent.TITLE_PLUS_POLICY_STARTED);
		}
		this.dialog.open(TitlePlusModalComponent);
	}

	cancel() {
		const data: TitlePlusCancelModalData = {
			status: this.status,
		};

		this.dialog
			.open(TitlePlusCancelModalComponent, {
				data,
				maxWidth: '450px',
				width: '90%',
			})
			.afterClosed()
			.pipe(
				untilDestroyed(this),
				take(1),
				filter((result) => !!result),
			)
			.subscribe(() => {
				if (!this.status || this.status === TitlePlusStatus.NOT_SUBMITTED) {
					this.matterDispatch.SetTitleInsurance(
						this.matterId,
						this.blankTitleInsurance,
					);
				} else {
					this.dialog.open(TitlePlusModalComponent);
				}
			});
	}

	selectInsuranceProvider() {
		this.dialog.open(AddTitleInsuranceComponent);
	}

	setCurrentStep(status: TitlePlusStatus): void {
		switch (status) {
			case TitlePlusStatus.NOT_SUBMITTED:
				this.currentStep = 0;
				break;
			case TitlePlusStatus.UNDER_REVIEW:
			case TitlePlusStatus.SUBMITTED:
			case TitlePlusStatus.USER_ACTION_REQUIRED:
				this.currentStep = 2;
				break;
			case TitlePlusStatus.CANCELLED:
			case TitlePlusStatus.DECLINED:
			case TitlePlusStatus.DELETED:
			case TitlePlusStatus.ISSUED:
				this.currentStep = 3;
				break;
			default:
				this.currentStep = 1;
				break;
		}
	}

	changeStepperColor(state?: string): void {
		switch (state) {
			case 'failed':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: true,
					warning: false,
				}));
				break;
			case 'warning':
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: true,
				}));
				break;
			default:
				this.steps = this.steps.map((step) => ({
					...step,
					failed: false,
					warning: false,
				}));
				break;
		}
	}

	setAllowSwitchProviders(status: TitlePlusStatus) {
		this.allowSwitchProviders = [
			TitlePlusStatus.NOT_SUBMITTED,
			TitlePlusStatus.CANCELLED,
			TitlePlusStatus.DECLINED,
			TitlePlusStatus.DELETED,
		].includes(status);
	}
}
